import React, { useState, useEffect, useCallback, useReducer } from "react";
import { View, Text, StyleSheet, TouchableOpacity, Dimensions } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import IconPickerView from "./IconPickerView";
import ColorSwatchView from "./ColorSwatchView";
import StylerView from "./StylerView";
import ComponentCreatorView from "./ComponentCreatorView";

// Modal modes
export const MODAL_MODES = {
	ICON_PICKER: "icon-picker",
	COLOR_SWATCH: "color-swatch",
	STYLER: "styler",
	COMPONENT_CREATOR: "component-creator",
};

// --- Reducers ---

// --- Component Config Reducer (Moved from ComponentCreatorView) ---
const initialComponentConfigState = {
	header: "Reading Progress",
	size: "half",
	dataType: "progressbar",
	dataConfig: {
		maxValue: 52,
		showPercentage: true,
		unit: "books read",
		iconName: null, // Add iconName here
	},
};

function componentConfigReducer(state, action) {
	switch (action.type) {
		case "UPDATE_CONFIG":
			// Ensure dataConfig exists if it doesn't
			const currentDataConfig = state.dataConfig || {};
			return { ...state, ...action.payload, dataConfig: currentDataConfig };
		case "UPDATE_DATA_CONFIG":
			// Ensure dataConfig exists before trying to spread into it
			const existingDataConfig = state.dataConfig || {};
			return {
				...state,
				dataConfig: { ...existingDataConfig, ...action.payload },
			};
		case "CHANGE_DATA_TYPE":
			// Reset dataConfig when type changes, but preserve icon if switching TO icon type
			const newDataType = action.payload;
			const preservedIconName = newDataType === "icon" ? state.dataConfig?.iconName : null;
			const baseDataConfig = preservedIconName ? { iconName: preservedIconName } : {};

			return {
				...state,
				dataType: newDataType,
				// Initialize specific fields for the new type if needed, keeping iconName if applicable
				dataConfig: baseDataConfig,
			};
		case "SET_ICON": // New action specifically for icon picker
			return {
				...state,
				dataType: "icon", // Ensure dataType is icon when setting one
				dataConfig: { ...state.dataConfig, iconName: action.payload.iconName },
			};
		case "RESET_CONFIG":
			return initialComponentConfigState;
		default:
			return state;
	}
}
// --- End Component Config Reducer ---

// Initial State and Reducer for Styles
const initialStylesState = {
	typographyStyles: [
		{ id: "heading1", name: "Heading 1", fontFamily: "Arial", fontSize: 28, fontWeight: "700", lineHeight: 1.2, color: "#FFFFFF" },
		{ id: "heading2", name: "Heading 2", fontFamily: "Arial", fontSize: 22, fontWeight: "600", lineHeight: 1.3, color: "#FFFFFF" },
		{ id: "body", name: "Body Text", fontFamily: "System", fontSize: 16, fontWeight: "400", lineHeight: 1.5, color: "#FFFFFF" },
		{ id: "caption", name: "Caption", fontFamily: "System", fontSize: 12, fontWeight: "400", lineHeight: 1.4, color: "#9E9E9E" },
	],
	editingStyleId: null,
	editingStyleSnapshot: null, // Holds temporary changes while editing
};

function stylesReducer(state, action) {
	switch (action.type) {
		case "START_EDITING":
			const styleToEdit = state.typographyStyles.find((s) => s.id === action.payload.styleId);
			return { ...state, editingStyleId: action.payload.styleId, editingStyleSnapshot: { ...styleToEdit } };
		case "UPDATE_EDITING_STYLE":
			if (!state.editingStyleSnapshot) return state;
			return { ...state, editingStyleSnapshot: { ...state.editingStyleSnapshot, ...action.payload.changes } };
		case "UPDATE_EDITING_STYLE_COLOR": // Specific action for color updates after picker
			if (!state.editingStyleSnapshot) return state;
			return { ...state, editingStyleSnapshot: { ...state.editingStyleSnapshot, color: action.payload.color } };
		case "SAVE_STYLE":
			if (!state.editingStyleSnapshot) return state;
			const updatedStyles = state.typographyStyles.map((style) => (style.id === state.editingStyleId ? state.editingStyleSnapshot : style));
			console.log("Updated Typography Styles (saved):", updatedStyles); // Log saved styles
			return { ...state, typographyStyles: updatedStyles, editingStyleId: null, editingStyleSnapshot: null };
		case "CANCEL_EDIT":
			return { ...state, editingStyleId: null, editingStyleSnapshot: null };
		case "CREATE_STYLE":
			const newId = `custom-${Date.now()}`;
			const newStyle = {
				id: newId,
				name: "New Style",
				fontFamily: "System",
				fontSize: 16,
				fontWeight: "400",
				lineHeight: 1.5,
				color: "#FFFFFF",
			};
			return {
				...state,
				typographyStyles: [...state.typographyStyles, newStyle],
				editingStyleId: newId,
				editingStyleSnapshot: { ...newStyle },
			};
		default:
			return state;
	}
}

// Initial State and Reducer for Color Picker Context
const initialColorState = {
	selectedColor: null, // The color currently chosen in the swatch
	initialColorForPicker: null, // The color passed when opening the picker
	colorPickerSource: null, // e.g., 'styler' to know who opened it
};

function colorReducer(state, action) {
	switch (action.type) {
		case "START_COLOR_PICKING":
			return { ...state, initialColorForPicker: action.payload.initialColor, colorPickerSource: action.payload.source, selectedColor: action.payload.initialColor };
		case "SET_SELECTED_COLOR":
			return { ...state, selectedColor: action.payload.color };
		case "RESET_COLOR_PICKER":
			return { ...state, initialColorForPicker: null, colorPickerSource: null }; // Keep selectedColor potentially? Or reset? Let's reset for now. selectedColor: null
		default:
			return state;
	}
}

// Initial State and Reducer for Icon Picker Context
const initialIconState = {
	selectedIcon: null,
	callback: null, // Add callback to store
};

function iconReducer(state, action) {
	switch (action.type) {
		case "SELECT_ICON":
			return { ...state, selectedIcon: action.payload };
		case "SET_CALLBACK":
			return { ...state, callback: action.payload };
		case "RESET":
			return initialIconState;
		default:
			return state;
	}
}

// --- Component ---

const EditorModal = ({ visible, onClose, initialMode = MODAL_MODES.ICON_PICKER, onComplete }) => {
	// State for modal UI (visibility, current mode, breadcrumbs)
	const [activeMode, setActiveMode] = useState(initialMode);
	const [previousMode, setPreviousMode] = useState(null);
	const [showBreadcrumb, setShowBreadcrumb] = useState(false);

	// Use Reducers for core data state
	const [stylesState, stylesDispatch] = useReducer(stylesReducer, initialStylesState);
	const [colorState, colorDispatch] = useReducer(colorReducer, initialColorState);
	const [componentConfigState, componentConfigDispatch] = useReducer(componentConfigReducer, initialComponentConfigState);
	const [iconState, iconDispatch] = useReducer(iconReducer, initialIconState);

	// Reset state when modal visibility changes
	useEffect(() => {
		if (visible) {
			setActiveMode(initialMode);
			setPreviousMode(null);
			setShowBreadcrumb(false);
			stylesDispatch({ type: "CANCEL_EDIT" });
			colorDispatch({ type: "RESET_COLOR_PICKER" });
			iconDispatch({ type: "RESET" });
		}
	}, [visible, initialMode]);

	// Handler to open color picker (called FROM StylerView)
	const handleOpenColorPicker = useCallback(
		(currentColor) => {
			setPreviousMode(activeMode); // Track where we came from
			colorDispatch({ type: "START_COLOR_PICKING", payload: { initialColor: currentColor, source: "styler" } });
			setShowBreadcrumb(true);
			setActiveMode(MODAL_MODES.COLOR_SWATCH);
		},
		[activeMode]
	);

	// Handler when a color is selected IN ColorSwatchView
	const handleColorSelected = useCallback(
		(color) => {
			colorDispatch({ type: "SET_SELECTED_COLOR", payload: { color } });

			// If the color picker was opened by the styler, update the editing style immediately
			if (colorState.colorPickerSource === "styler") {
				stylesDispatch({ type: "UPDATE_EDITING_STYLE_COLOR", payload: { color } });
			}

			// Navigate back
			if (previousMode) {
				setActiveMode(previousMode);
				setPreviousMode(null); // Reset previous mode
				setShowBreadcrumb(false); // Hide breadcrumb
				colorDispatch({ type: "RESET_COLOR_PICKER" }); // Reset color picker context after use
			} else {
				// If no previous mode (shouldn't happen with current flow), maybe close or go to default?
				onClose();
			}
		},
		[previousMode, colorState.colorPickerSource, onClose] // Dependencies
	);

	// --- Icon Picker Handlers ---
	const handleOpenIconPicker = useCallback(
		(callback) => {
			setPreviousMode(activeMode);
			setShowBreadcrumb(true);
			setActiveMode(MODAL_MODES.ICON_PICKER);
			// Store the callback for when icon is selected
			iconDispatch({ type: "SET_CALLBACK", payload: callback });
		},
		[activeMode]
	);

	const handleIconSelected = useCallback(
		(iconName) => {
			// Just update the selected icon state
			iconDispatch({ type: "SELECT_ICON", payload: iconName });

			// If from component creator, also update component config
			if (previousMode === MODAL_MODES.COMPONENT_CREATOR) {
				componentConfigDispatch({ type: "SET_ICON", payload: { iconName } });
			}
		},
		[previousMode]
	);

	// Handler for the main 'Apply' button
	const handleApply = () => {
		if (activeMode === MODAL_MODES.ICON_PICKER) {
			if (iconState.selectedIcon) {
				// Call the stored callback if it exists
				if (iconState.callback) {
					iconState.callback(iconState.selectedIcon);
				} else if (onComplete) {
					onComplete(iconState.selectedIcon);
				}

				// If we came from the component creator, go back instead of closing
				if (previousMode === MODAL_MODES.COMPONENT_CREATOR) {
					handleGoBack();
					return;
				}
			}
		} else if (activeMode === MODAL_MODES.COMPONENT_CREATOR && onComplete) {
			console.log("Saving component with config:", componentConfigState);
			onComplete(componentConfigState);
		}

		onClose();
	};

	if (!visible) return null;

	// Render content based on active mode, passing state and dispatch
	const renderContent = () => {
		switch (activeMode) {
			case MODAL_MODES.ICON_PICKER:
				return <IconPickerView onSelectIcon={handleIconSelected} selectedIcon={iconState.selectedIcon} />;
			case MODAL_MODES.COLOR_SWATCH:
				return <ColorSwatchView onSelectColor={handleColorSelected} initialColor={colorState.initialColorForPicker} selectedColor={colorState.selectedColor} />;
			case MODAL_MODES.STYLER:
				return <StylerView typographyStyles={stylesState.typographyStyles} editingStyle={stylesState.editingStyleSnapshot} stylesDispatch={stylesDispatch} onOpenColorModal={handleOpenColorPicker} />;
			case MODAL_MODES.COMPONENT_CREATOR:
				return <ComponentCreatorView config={componentConfigState} dispatch={componentConfigDispatch} onOpenIconPicker={handleOpenIconPicker} />;
			default:
				return <IconPickerView onSelectIcon={handleIconSelected} selectedIcon={iconState.selectedIcon} />;
		}
	};

	// --- Rest of the component (getTitle, getIcon, JSX structure) remains similar ---
	// Need to adjust the back button logic if it relied on state removed/changed.

	const getModalTitle = () => {
		switch (activeMode) {
			case MODAL_MODES.ICON_PICKER:
				// Make title specific if coming from component creator
				return showBreadcrumb && previousMode === MODAL_MODES.COMPONENT_CREATOR ? "Select Icon for Component" : "Icon Picker";
			case MODAL_MODES.COLOR_SWATCH:
				// Use colorPickerSource to potentially make title more specific
				return showBreadcrumb && colorState.colorPickerSource === "styler" ? "Select Color for Style" : "Color Swatch";
			case MODAL_MODES.STYLER:
				return "Typography & Styling";
			case MODAL_MODES.COMPONENT_CREATOR:
				return "Component Creator"; // Add title for component creator
			default:
				return "Select";
		}
	};

	const getModalIcon = () => {
		switch (activeMode) {
			case MODAL_MODES.ICON_PICKER:
				return "apps-outline";
			case MODAL_MODES.COLOR_SWATCH:
				return "color-palette-outline";
			case MODAL_MODES.STYLER:
				return "text-outline";
			case MODAL_MODES.COMPONENT_CREATOR:
				return "construct-outline"; // Add icon for component creator
			default:
				return "apps-outline";
		}
	};

	// Back button navigation logic
	const handleGoBack = () => {
		if (previousMode) {
			const leavingMode = activeMode;
			setActiveMode(previousMode);
			// Reset context related to the mode we are leaving
			if (leavingMode === MODAL_MODES.COLOR_SWATCH) {
				colorDispatch({ type: "RESET_COLOR_PICKER" });
			}
			// No specific reset needed for icon picker state itself when just going back
			setPreviousMode(null);
			setShowBreadcrumb(false);
		}
	};

	return (
		<View style={styles.modalOverlay}>
			<View style={styles.modalContainer}>
				<View style={styles.modalHeader}>
					<View style={styles.titleContainer}>
						{showBreadcrumb && previousMode && (
							<TouchableOpacity style={styles.breadcrumbButton} onPress={handleGoBack}>
								<Ionicons name="arrow-back" size={20} color="#fff" />
								{/* Adjust breadcrumb text based on previousMode */}
								<Text style={styles.breadcrumbText}>{previousMode === MODAL_MODES.STYLER ? "Typography" : previousMode === MODAL_MODES.COMPONENT_CREATOR ? "Creator" : "Back"}</Text>
							</TouchableOpacity>
						)}
						<Ionicons name={getModalIcon()} size={24} color="#fff" style={styles.titleIcon} />
						<Text style={styles.modalTitle}>{getModalTitle()}</Text>
					</View>

					<TouchableOpacity style={styles.closeButton} onPress={onClose}>
						<Ionicons name="close" size={24} color="#fff" />
					</TouchableOpacity>
				</View>

				<View style={styles.modalContent}>{renderContent()}</View>

				<View style={styles.modalFooter}>
					<TouchableOpacity style={styles.cancelButton} onPress={onClose}>
						<Text style={styles.cancelButtonText}>Cancel</Text>
					</TouchableOpacity>
					{/* Apply button might only be relevant for certain modes now */}
					{activeMode === MODAL_MODES.ICON_PICKER || activeMode === MODAL_MODES.COMPONENT_CREATOR ? (
						<TouchableOpacity style={styles.applyButton} onPress={handleApply}>
							<Text style={styles.applyButtonText}>{activeMode === MODAL_MODES.COMPONENT_CREATOR ? "Save Component" : "Apply"}</Text>
						</TouchableOpacity>
					) : null}
				</View>
			</View>
		</View>
	);
};

const windowWidth = Dimensions.get("window").width;
const windowHeight = Dimensions.get("window").height;

const styles = StyleSheet.create({
	modalOverlay: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.7)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 2000,
	},
	modalContainer: {
		width: "80%",
		maxWidth: 1000, // Allow wider modal
		height: "80%",
		maxHeight: 700, // Limit height
		backgroundColor: "#1E1E1E", // Use the slightly different background
		borderRadius: 8,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
		backgroundColor: "#2a2a2a", // Darker header
	},
	titleContainer: {
		flexDirection: "row",
		alignItems: "center",
		flex: 1, // Allow title container to take space
	},
	titleIcon: {
		marginRight: 10,
	},
	modalTitle: {
		color: "#fff",
		fontSize: 16,
		fontWeight: "600",
	},
	breadcrumbButton: {
		flexDirection: "row",
		alignItems: "center",
		marginRight: 12,
		paddingRight: 12,
		borderRightWidth: 1,
		borderRightColor: "#444",
	},
	breadcrumbText: {
		color: "#aaa",
		fontSize: 14,
		marginLeft: 6,
	},
	closeButton: {
		padding: 4,
	},
	modalContent: {
		flex: 1, // Ensure content area fills available space
		backgroundColor: "#1A1A1A", // Match Editor background? Or keep #1E1E1E?
	},
	modalFooter: {
		flexDirection: "row",
		justifyContent: "flex-end",
		padding: 16,
		borderTopWidth: 1,
		borderTopColor: "#333",
		backgroundColor: "#2a2a2a", // Darker footer
	},
	cancelButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
		backgroundColor: "#444",
		marginRight: 10,
	},
	cancelButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	applyButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
		backgroundColor: "#0E98FF", // Use a primary color
	},
	applyButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
});

export default EditorModal;
